/**
 * Receiver for the customer-supplied `tracking` object.
 *
 * The widget reads `window.magma_app[].tracking` on the customer's own page, base64url-encodes it,
 * and appends it to the signup iframe URL as `?tracking=…`. This module decodes it back.
 *
 * THIS SIDE IS AUTHORITATIVE. Direct links to `/helpee-signup/:campaignUuid?tracking=…` are
 * hand-crafted by customers and never pass through the widget's encoder, so every limit is
 * re-enforced here rather than trusted.
 *
 * Nothing here may throw: a malformed value must degrade to "no tracking", never break a signup.
 *
 * Keep the constants below in sync with `magma-integrations/src/utils/tracking.ts` (the encoder) and
 * `magma-backend/src/utils/tracking.ts` (the storage sanitizer).
 */
import type { TrackingType } from '@magma-app/magma-types/commons';

/** 1536 bytes of JSON encodes to exactly 2048 base64 chars (ceil(n/3)*4). */
const MAX_ENCODED_CHARS = 2048;
const MAX_KEYS = 30;
const MAX_DEPTH = 3;
const MAX_KEY_LENGTH = 128;
const MAX_VALUE_LENGTH = 512;
const MAX_ARRAY_LENGTH = 20;

const DENIED_KEYS = ['__proto__', 'constructor', 'prototype'];

/**
 * The public type comes from magma-types so the request payload and this decoder can never drift.
 * `TrackingType` encodes the depth-3 limit as explicit nesting levels.
 */
type Tracking = TrackingType;

/**
 * The internal working type is recursive, because the sanitizer below is recursive. It is NOT
 * assignable to the depth-bounded `Tracking`, so `sanitizeTracking` casts once at its boundary —
 * sound because `MAX_DEPTH` is enforced at runtime before that cast happens.
 */
type TrackingValue =
  | string
  | number
  | boolean
  | null
  | TrackingValue[]
  | { [key: string]: TrackingValue };

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Truncate first, then normalize. `slice` works on UTF-16 code units and can cut a surrogate pair in
 * half; the TextEncoder/TextDecoder round-trip then replaces any unpaired surrogate with U+FFFD.
 * Doing it the other way round would reintroduce exactly the problem this removes — and Postgres
 * `jsonb` rejects lone surrogates outright, so it has to be handled before the value is sent.
 */
const cleanString = (value: string, maxLength: number): string => {
  const truncated = value.replace(/\0/g, '').slice(0, maxLength);
  return new TextDecoder().decode(new TextEncoder().encode(truncated));
};

const cleanKey = (key: string): string | null => {
  const cleaned = cleanString(key, MAX_KEY_LENGTH).trim();
  if (!cleaned) return null;
  if (DENIED_KEYS.includes(cleaned)) return null;
  return cleaned;
};

/** Returns `undefined` for values that cannot be represented and should be dropped. */
const cleanValue = (value: unknown, depth: number): TrackingValue | undefined => {
  if (value === null) return null;

  const type = typeof value;
  if (type === 'string') return cleanString(value as string, MAX_VALUE_LENGTH);
  if (type === 'boolean') return value as boolean;
  if (type === 'number') return Number.isFinite(value) ? (value as number) : undefined;
  if (type !== 'object') return undefined;
  if (depth >= MAX_DEPTH) return undefined;

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => cleanValue(item, depth + 1))
      .filter((item): item is TrackingValue => item !== undefined);
  }

  const nested: Record<string, TrackingValue> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
    const key2 = cleanKey(key);
    if (key2 === null) return;
    const cleaned = cleanValue(nestedValue, depth + 1);
    if (cleaned === undefined) return;
    nested[key2] = cleaned;
  });
  return nested;
};

const sanitizeTracking = (input: unknown): Tracking | undefined => {
  if (!isPlainObject(input)) return undefined;

  const cleaned: Record<string, TrackingValue> = {};
  Object.keys(input).forEach((key) => {
    if (Object.keys(cleaned).length >= MAX_KEYS) return;
    const key2 = cleanKey(key);
    if (key2 === null) return;
    const value = cleanValue(input[key], 1);
    if (value === undefined) return;
    cleaned[key2] = value;
  });

  if (Object.keys(cleaned).length === 0) return undefined;

  // Safe: `cleanValue` drops anything at or beyond MAX_DEPTH, so the runtime shape already satisfies
  // the depth-bounded `Tracking` type even though the recursive working type cannot prove it.
  return cleaned as Tracking;
};

const decodeTracking = (raw: string): Tracking | undefined => {
  try {
    // Length check BEFORE atob: a hand-crafted multi-megabyte URL should cost nothing to reject.
    if (!raw || raw.length > MAX_ENCODED_CHARS) return undefined;

    // base64url -> base64, then restore the padding atob requires.
    let base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) base64 += '=';

    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    return sanitizeTracking(JSON.parse(json));
  } catch {
    // Invalid base64, invalid UTF-8, invalid JSON, an array, a primitive — all mean "no tracking".
    return undefined;
  }
};

let cached: Tracking | undefined;
let hasRead = false;

/**
 * Read the tracking object for this signup session. Memoized, including the miss.
 *
 * Memoization is load-bearing, not an optimization: `HelpeeMultiCampaignSignUpView` navigates with
 * `router.push({ name: 'helpee-signup', params: … })` and NO query, which drops `?tracking=`. Caching
 * on first read makes the value immune to that and to any future query rewrite.
 *
 * Reads `window.location.search` directly rather than `route.query`, matching the existing
 * `extractPhoneNumberFromUrl` precedent in `useHelpeeSignupBootstrap` — it dodges router
 * normalization and works before the router is ready.
 */
const getTracking = (): Tracking | undefined => {
  if (hasRead) return cached;
  hasRead = true;

  try {
    const params = new URLSearchParams(window.location.search);
    // Case-insensitive, consistent with the other query-param lookups in the signup flow.
    const key = Array.from(params.keys()).find((k) => k.toLowerCase() === 'tracking');
    cached = key ? decodeTracking(params.get(key) as string) : undefined;
  } catch {
    cached = undefined;
  }

  return cached;
};

export { getTracking, decodeTracking, sanitizeTracking, MAX_ENCODED_CHARS };
export type { Tracking, TrackingValue };
